<template>
  <Modal
    :open="displayModal"
    title="Edit Video Details"
    :showEscapeButton="false"
    :showAcceptButton="true"
    acceptButtonText="Save"
    :showDangerButton="false"
    @close="displayModal = false"
    @onSuccess="editVideo"
    ref="modal"
    v-if="displayModal"
  >
    <YouTubePlayer
      :youtube_id="data.youtube_id"
      class="rounded-lg overflow-hidden"
    />

    <SegmentedControl
      :tabs="segmentedControlTabs"
      @selectedTab="tabSelected"
      class="mt-4"
    />

    <div class="grid grid-cols-3 gap-4 mt-4" v-if="activeTab === 'general'">
      <div class="col-span-3">
        <label for="title" class="block text-sm font-medium text-gray-700"
          >Video Title
          <span class="text-xs font-normal text-gray-400"
            >{{ data.title | count }} characters remaining</span
          >
        </label>
        <input
          type="text"
          name="title"
          id="title"
          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          v-model="data.title"
        />
      </div>

      <div class="col-span-3 md:col-span-1">
        <label for="price" class="block text-sm font-medium text-gray-700"
          >Price</label
        >
        <input
          type="number"
          name="price"
          id="price"
          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          v-model="data.price"
        />
      </div>

      <div class="col-span-3 md:col-span-2">
        <label for="rating" class="block text-sm font-medium text-gray-700"
          >Rating</label
        >
        <div class="flex">
          <div class="w-1/2">
            <input
              type="text"
              name="rating"
              id="rating"
              class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              v-model="data.rating"
              placeholder="3.5"
            />
          </div>
          <div class="w-1/2 flex justify-center items-center">
            <StarRating
              id="starRating"
              :rating="data.rating > 5 ? 0 : data.rating"
            />
          </div>
        </div>
      </div>

      <div class="col-span-3">
        <label for="description" class="block text-sm font-medium text-gray-700"
          >Video Narrative
        </label>
        <textarea
          type="textarea"
          name="description"
          id="description"
          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          rows="5"
          v-model="data.description"
        ></textarea>
      </div>
    </div>

    <div v-if="activeTab === 'network-pricing'">
      <div class="-mx-1 my-2">
        <span
          class="m-1 px-3 py-1 bg-blue-500 text-white text-sm rounded-full inline-block cursor-pointer hover:bg-blue-400 shadow-md"
          v-for="network in networksToDisplayAsAvailable"
          v-bind:key="network.id"
          v-show="network.displayPricingDefault"
          v-on:click="selectNetwork(network)"
        >
          <i class="fa fa-plus mr-1"></i>{{ network.name }}
        </span>
        <!-- <span
          class="m-1 px-3 py-1 bg-gray-500 text-white text-sm rounded-full inline-block cursor-pointer hover:bg-gray-400 shadow-md"
        >
          <i class="fa fa-magnifying-glass mr-1"></i> Search
        </span> -->
      </div>

      <div class="mt-4" v-if="selectedNetworks.length == 0">
        No networks currently selected.
      </div>

      <div class="mt-4 flex flex-col space-y-2">
        <div
          class="flex flex-row items-center content-center justify-between bg-gray-100 p-2 pl-4 rounded-lg"
          v-for="(network, index) in selectedNetworks"
          v-bind:key="network.id"
        >
          <div class="flex flex-row items-center content-center space-x-2">
            <i
              class="fa-regular fa-circle-minus cursor-pointer hover:text-gray-400"
              v-on:click="removeFromSelectedNetworks(index, network.id)"
            ></i>
            <div class=" flex items-center content-center">
              {{ network.name }}
            </div>
          </div>
          <div class="">
            <input
              class="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              type="number"
              v-model="perNetworkPricing[network.id]"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-3 gap-4 mt-4" v-if="activeTab === 'advanced'">
      <div class="col-span-3">
        <label for="price" class="block text-sm font-medium text-gray-700"
          >Video Owner</label
        >
        <p class="text-sm text-gray-700 py-1">
          {{ data.user.name }}
          <span class="text-xs text-gray-500">({{ data.user.email }})</span>
        </p>
        <SearchListBox
          :dataArr="userCollection"
          placeholder="Type to search for a user..."
          resId="user"
          textKey="name"
          @onSearch="onSearch"
          @onSelect="onSelectItem"
        />
      </div>
      <div class="col-span-1">
        <label for="type" class="block text-sm font-medium text-gray-700"
          >Video Type</label
        >
        <select
          class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          v-model="data.type"
        >
          <option value="eng">B-Roll (ENG)</option>
          <option value="stock">Stock</option>
        </select>
      </div>

      <div class="col-span-1">
        <label for="type" class="block text-sm font-medium text-gray-700"
          >YouTube Visibility</label
        >
        <select
          class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          v-model="data.visibility"
        >
          <option value="public">Public</option>
          <option value="unlisted">Unlisted</option>
          <option value="privae">Private</option>
        </select>
      </div>

      <div class="col-span-1">
        <label for="youtube_id" class="block text-sm font-medium text-gray-700"
          >YouTube ID</label
        >
        <input
          type="text"
          name="youtube_id"
          id="youtube_id"
          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          v-model="data.youtube_id"
        />
      </div>

      <div class="col-span-3">
        <label for="embargo" class="block text-sm font-medium text-gray-700"
          >Embargo
        </label>
        <input
          type="text"
          name="embargo"
          id="embargo"
          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          v-model="data.embargo"
        />
      </div>

      <div class="col-span-3 flex flex-row space-x-4 items-center">
        <input
          name="excludeFromCreditAgreements"
          id="excludeFromCreditAgreements"
          type="checkbox"
          class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded focus:outline-none"
          v-model="data.exempt_from_credit_agreements"
        />
        <label for="excludeFromCreditAgreements" class="sm:text-sm">
          Exclude this video from all credit agreements.
        </label>
      </div>

      <!-- <div class="col-span-3">
        <label
          for="decline_message"
          class="block text-sm font-medium text-gray-700"
          >Decline Message
        </label>
        <textarea
          type="textarea"
          name="decline_message"
          id="decline_message"
          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          rows="2"
          v-model="data.decline_message"
        ></textarea>
      </div> -->

      <div class="col-span-3">
        <button
          class="px-4 py-2 text-white text-center font-medium bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"
          v-on:click="downloadWatermarkedVideo()"
        >
          Download Watermarked Preview
        </button>
      </div>
    </div>
  </Modal>
</template>

<script>
import * as Config from "@/config";

import { mapActions } from "vuex";

import Modal from "@/components/Modal";
import YouTubePlayer from "@/components/media/components/YouTubePlayer";
import StarRating from "@/components/general/StarRating";
import SearchListBox from "@/components/SearchListBox";
import SegmentedControl from "@/components/general/SegmentedControl";

export default {
  data() {
    return {
      displayModal: false,
      modal: false,
      data: {},
      userCollection: [],
      activeTab: "general",
      networks: [],
      selectedNetworks: [],
      segmentedControlTabs: [
        {
          name: "General",
          type: "general"
        },
        {
          name: "Network Pricing",
          type: "network-pricing"
        },
        {
          name: "Advanced",
          type: "advanced"
        }
      ],
      perNetworkPricing: {},
      data: {}
    };
  },

  components: {
    Modal,
    YouTubePlayer,
    StarRating,
    SearchListBox,
    SegmentedControl
  },

  mounted() {
    this.$nextTick(
      function() {
        this.$eventBus.$on("admin-edit-video-open-modal", this.open);
      }.bind(this)
    );
    this.getNetworks();
  },

  destroyed() {
    this.$eventBus.$off("admin-edit-video-open-modal", this.open);
  },

  watch: {
    rating: function(val) {
      if (/[a-zA-Z]/.test(val)) {
        this.$swal(
          "Whoops!",
          "Your rating must not contain any letters.",
          "error"
        );
      }
      if (val < 0 || val > 5) {
        this.$swal("Whoops!", "Your rating must be between 0 and 5.", "error");
      }
    },
    perNetworkPricing: function(data) {
      if (this.data.perNetworkPrice !== null && data.length >= 0) {
        this.perNetworkPricing = {};
      }
    }
  },

  computed: {
    networksToDisplayAsAvailable() {
      let selectedNetworksIds = this.selectedNetworks.map(a => a.id);

      return this.networks.filter(item => {
        return selectedNetworksIds.indexOf(item.id) === -1;
      });
    }
  },

  methods: {
    ...mapActions(["addToastMessage"]),
    close() {
      this.displayModal = false;
    },

    open(data) {
      this.activeTab = "general";
      this.displayModal = true;
      this.data = data;
      this.selectedNetworksIds = [];
      this.selectedNetworks = [];
      this.perNetworkPricing = {};

      if (this.data.perNetworkPrice !== null) {
        this.selectedNetworksIds = Object.keys(this.data.perNetworkPrice).map(
          key => {
            return parseInt(key);
          }
        );
        this.selectedNetworks = this.selectedNetworksIds.map(nId => {
          return this.networks.find(n => n.id === nId);
        });
        this.perNetworkPricing = this.data.perNetworkPrice;
      }
    },

    async getNetworks() {
      let res = await this.$http.get(
        Config.apiPath + "/api/v2/admin/networks?type=national"
      );
      this.networks = res.data;
    },

    async onSearch(searchText) {
      console.log(searchText);
      var data = {
        name: searchText
      };
      let res = await this.$http.post(
        Config.apiPath + "/api/v2/admin/users/search/stringers",
        data
      );
      this.userCollection = res.data;
    },
    async onSelectItem(item) {
      this.userCollection = []; // clear locations
      this.data.user = item;
      console.log(this.data.user);
    },
    tabSelected(tab) {
      this.activeTab = tab;
    },
    selectNetwork(network) {
      this.selectedNetworks.push(network);
    },
    removeFromSelectedNetworks(index, networkId) {
      this.selectedNetworks.splice(index, 1);
      delete this.perNetworkPricing[networkId];
    },
    async editVideo() {
      console.log("click");

      var data = {
        id: this.data.id,
        price: this.data.price,
        visibility: this.data.visibility,
        description: this.data.description,
        type: this.data.type,
        user: this.data.user,
        embargo: this.data.embargo,
        title: this.data.title,
        rating: this.data.rating,
        perNetworkPricing: this.perNetworkPricing,
        youtube_id: this.data.youtube_id,
        exempt_from_credit_agreements: this.data.exempt_from_credit_agreements
      };
      try {
        let res = await this.$http.post(
          Config.apiPath + "/api/v2/admin/videos/edit/" + this.data.id,
          data
        );

        this.addToastMessage({
          title: "Success",
          text: "Video was successfully updated!",
          type: "success"
        });
        this.close();
      } catch (e) {
        this.addToastMessage({
          title: "Error",
          text:
            "There has been an error editing the details of this video, try again.",
          type: "error"
        });
      }
    },
    downloadWatermarkedVideo() {
      var link = document.createElement("a");
      document.body.appendChild(link);
      link.href = this.data.watermarkedUrl;
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>
